import { PageHero } from '@components/common';
import { ImageCardGrid } from '@components/sections';
import { ArrowRight, Brain, Link2, FileCheck, Briefcase, Umbrella, Activity } from 'lucide-react';
import { CALENDLY_URL } from '@constants';
import { Link } from 'react-router-dom';
import { serviceCards } from '@data/serviceCards';

const urgencyItems = [
  {
    icon: Brain,
    title: 'AI Adoption Without Governance',
    description:
      'Generative AI is being adopted faster than policies can keep up. The organisations that govern it early gain competitive advantage. Those that wait face unquantified risk.',
  },
  {
    icon: Link2,
    title: 'Supply Chain Dependency',
    description:
      'Your security is only as strong as your weakest vendor. Hidden exposure in third-party relationships is now a board-level concern - and a due diligence red flag.',
  },
  {
    icon: FileCheck,
    title: 'Regulatory Scrutiny Is Expanding',
    description:
      'NIS2, DORA, the UK Cyber Governance Code of Practice - the regulatory net is widening. Compliance is no longer a checkbox; it is a leadership accountability.',
  },
  {
    icon: Briefcase,
    title: 'Customer & Investor Due Diligence',
    description:
      'Enterprise buyers and investors now demand evidence of board-level security accountability. A named CISO and documented controls can make or break a deal.',
  },
  {
    icon: Umbrella,
    title: 'Cyber Insurance Pressure',
    description:
      'Premiums are rising and insurers are demanding demonstrable controls, incident response capability, and experienced leadership. Without them, coverage shrinks or disappears.',
  },
  {
    icon: Activity,
    title: 'Operational Resilience Expectations',
    description:
      'Regulators, customers, and boards now expect you to prove you can continue operating through disruption. Not if - how prepared you are when it happens.',
  },
];

export default function ServicesPillarPage() {
  // Service structured data for SEO
  const servicesStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: serviceCards.map((service, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Service',
        name: service.title,
        description: service.description,
        provider: {
          '@type': 'Organization',
          name: 'The CISO Network',
        },
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(servicesStructuredData) }}
      />
      <PageHero
        title="Fractional CISO Services"
        subtitle="Comprehensive cybersecurity leadership delivered on-demand. From strategy to incident response, we provide the expertise you need without the full-time cost."
        eyebrow="Our Services"
        seoTitle="Fractional CISO Services | The CISO Network"
        seoDescription="Comprehensive fractional CISO services including security strategy, risk assessment, compliance, incident response, security culture, and board reporting."
      />

      {/* Services Overview */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              End-to-End Security Leadership
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Our{' '}
              <Link to="/what-is-a-fractional-ciso" className="text-secondary hover:underline">
                fractional CISOs
              </Link>{' '}
              deliver enterprise-grade security capabilities across nine core service areas. Each
              service can stand alone or work together as part of a comprehensive security
              programme.
            </p>
          </div>

          <ImageCardGrid cards={serviceCards} />
        </div>
      </section>

      {/* Why Fractional CISO */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Why Choose a Fractional CISO?
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="h-10 w-10 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-teal font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">
                      Cost-Effective Expertise
                    </h3>
                    <p className="text-muted-foreground">
                      Access senior CISO-level talent at a fraction of the cost of a full-time hire.
                      Pay for the time you need, not a full salary plus benefits.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="h-10 w-10 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-teal font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">
                      Immediate Impact
                    </h3>
                    <p className="text-muted-foreground">
                      No lengthy recruitment process. Our CISOs are ready to start immediately and
                      bring proven playbooks from day one.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="h-10 w-10 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-teal font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">
                      Scalable Engagement
                    </h3>
                    <p className="text-muted-foreground">
                      Scale up or down as your needs change. Perfect for growth phases, specific
                      projects, or temporary coverage.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="h-10 w-10 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-teal font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">
                      Objective Perspective
                    </h3>
                    <p className="text-muted-foreground">
                      External CISOs bring unbiased insights and industry best practices without
                      internal politics or legacy constraints.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <h3 className="text-2xl font-heading font-bold text-foreground mb-6">
                Typical Engagements
              </h3>
              <div className="space-y-4">
                <div className="p-4 bg-white rounded-xl border border-border">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-heading font-semibold text-foreground">
                      Advisory Retainer
                    </h4>
                    <span className="text-sm text-teal font-medium">Most Popular</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Ongoing strategic guidance, typically 2-4 days per month
                  </p>
                  <p className="text-sm font-medium text-foreground">From £1,800/month</p>
                </div>
                <div className="p-4 bg-white rounded-xl border border-border">
                  <h4 className="font-heading font-semibold text-foreground mb-2">Project-Based</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Fixed-scope engagements for specific initiatives like ISO 27001 or security
                    strategy
                  </p>
                  <p className="text-sm font-medium text-foreground">Custom pricing</p>
                </div>
                <div className="p-4 bg-white rounded-xl border border-border">
                  <h4 className="font-heading font-semibold text-foreground mb-2">Interim CISO</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Full-time coverage during transitions, recruitment, or leave periods
                  </p>
                  <p className="text-sm font-medium text-foreground">Variable rates</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Urgency Section */}
      <section className="py-24 gradient-navy relative overflow-hidden">
        <div className="container-custom relative z-10">
          <div className="text-center mb-12 animate-fade-up">
            <span className="text-sm font-medium text-teal tracking-wide uppercase">Why Now?</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mt-3 mb-4 max-w-3xl mx-auto">
              The Pressure Is Increasing. The Margin for Error Is Shrinking.
            </h2>
            <p className="text-primary-foreground/60 max-w-2xl mx-auto">
              Forces are converging on leadership teams right now. Organisations that move first
              gain confidence. Those that wait absorb risk.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {urgencyItems.map((item, i) => (
              <div
                key={i}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-teal/40 hover:bg-white/10 transition-all duration-300 animate-fade-up"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="h-10 w-10 rounded-lg bg-teal/20 flex items-center justify-center mb-4">
                  <item.icon className="h-5 w-5 text-teal" />
                </div>
                <h3 className="font-heading font-semibold text-primary-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-primary-foreground/70 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              How We Work
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our refined approach ensures seamless integration, trusted relationships, and outcomes
              that give you confidence from day one.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery Call',
                description:
                  'It starts with a conversation. We listen carefully to understand your business, security challenges, and ambitions.',
              },
              {
                step: '02',
                title: 'Understand Your Goals',
                description:
                  "We dig deeper to understand what success looks like for you - whether it's compliance, scaling, or responding to incidents.",
              },
              {
                step: '03',
                title: 'CISO Matching',
                description:
                  'We introduce you to the right CISO from our network - someone whose industry experience aligns with your needs.',
              },
              {
                step: '04',
                title: 'Ongoing Leadership',
                description:
                  'Your CISO provides continuous strategic security guidance, scaled to your pace and budget.',
              },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="text-5xl font-heading font-bold text-teal/20 mb-4">{item.step}</div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/how-we-work" className="btn-secondary-custom">
              Read More About Our Process
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Strengthen Your Security?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Every business is different. Book a discovery call and we'll help you identify the right
            services for your specific situation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
